import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { getItemById } from '../catalog/catalogManager';
import usePreferencesStore from './usePreferencesStore';

const useFurnitureStore = create(
  persist(
    (set, get) => ({
      furnitureItems: {},
      selectedFurnitureId: null,
      isDraggingFurniture: false,
      dragStartPos: null,

      addFurnitureItem: (itemId, position, rotation = 0, scale = 1) => {
        const itemTemplate = getItemById(itemId);
        if (!itemTemplate) return null;

        const preferences = usePreferencesStore.getState();
        const defaultStroke = preferences.objectStrokeColor;
        const defaultFill = preferences.objectFillColor;

        const furnitureId = uuidv4();
        const newFurnitureItem = {
          id: furnitureId,
          itemId: itemTemplate.id,
          x: position.x,
          y: position.y,
          rotation: rotation,
          scale: scale,
          width: itemTemplate.dimensions.width * scale,
          depth: itemTemplate.dimensions.depth * scale,
          flipX: false,
          flipY: false,
          lockAspectRatio: true,
          roomId: null,
          customProperties: {},
          strokeColor: defaultStroke || undefined,
          fillColor: defaultFill || undefined,
          strokeWidth: itemTemplate.render2D?.strokeWidth || 1 // ✅ Dodana domyślna grubość
        };

        set((state) => ({ furnitureItems: { ...state.furnitureItems, [furnitureId]: newFurnitureItem } }));
        return furnitureId;
      },
      
      duplicateFurnitureItem: (furnitureId) => {
        const { furnitureItems } = get();
        const item = furnitureItems[furnitureId];
        if (!item) return;
        
        const newId = uuidv4();
        const newItem = { ...item, id: newId, x: item.x + 20, y: item.y + 20 };
        set((state) => ({ furnitureItems: { ...state.furnitureItems, [newId]: newItem }, selectedFurnitureId: newId }));
      },
      
      removeFurnitureItem: (furnitureId) => {
        set((state) => {
          const newItems = { ...state.furnitureItems };
          delete newItems[furnitureId];
          return { furnitureItems: newItems, selectedFurnitureId: state.selectedFurnitureId === furnitureId ? null : state.selectedFurnitureId };
        });
      },
      
      bringFurnitureToFront: (furnitureId) => {
        set((state) => {
          const item = state.furnitureItems[furnitureId];
          if (!item) return {};
          const newItems = { ...state.furnitureItems };
          delete newItems[furnitureId];
          newItems[furnitureId] = item;
          return { furnitureItems: newItems };
        });
      },

      updateFurnitureItem: (furnitureId, updates) => {
        set((state) => {
          const item = state.furnitureItems[furnitureId];
          if (!item) return state;

          let finalUpdates = { ...updates };

          if (item.lockAspectRatio) {
            if (updates.width !== undefined && updates.depth === undefined) {
              const ratio = item.depth / item.width;
              finalUpdates.depth = updates.width * ratio;
            } else if (updates.depth !== undefined && updates.width === undefined) {
              const ratio = item.width / item.depth;
              finalUpdates.width = updates.depth * ratio;
            }
          }

          return {
            furnitureItems: {
              ...state.furnitureItems,
              [furnitureId]: { ...item, ...finalUpdates }
            }
          };
        });
      },
      
      flipFurnitureItem: (furnitureId, axis) => {
        const item = get().furnitureItems[furnitureId];
        if (!item) return;
        const updates = {};
        if (axis === 'horizontal') updates.flipX = !item.flipX;
        else if (axis === 'vertical') updates.flipY = !item.flipY;
        get().updateFurnitureItem(furnitureId, updates);
      },

      selectFurnitureItem: (furnitureId) => set({ selectedFurnitureId: furnitureId }),
      clearFurnitureSelection: () => set({ selectedFurnitureId: null }),
      startDraggingFurniture: (furnitureId, startPos) => set({ isDraggingFurniture: true, selectedFurnitureId: furnitureId, dragStartPos: startPos }),
      updateDraggingFurniture: (newPos) => {
        const { isDraggingFurniture, selectedFurnitureId } = get();
        if (!isDraggingFurniture || !selectedFurnitureId) return;
        get().updateFurnitureItem(selectedFurnitureId, { x: newPos.x, y: newPos.y });
      },
      stopDraggingFurniture: () => set({ isDraggingFurniture: false, dragStartPos: null }),

      rotateFurnitureItem: (furnitureId, deltaAngle) => {
        const item = get().furnitureItems[furnitureId];
        if (!item) return;
        const newRotation = item.rotation + deltaAngle;
        get().updateFurnitureItem(furnitureId, { rotation: newRotation });
      },

      scaleFurnitureItem: (furnitureId, scaleFactor) => {
        const item = get().furnitureItems[furnitureId];
        if (!item) return;
        const newScale = Math.max(0.1, Math.min(10, item.scale * scaleFactor));
        const effectiveFactor = newScale / item.scale;
        
        get().updateFurnitureItem(furnitureId, { 
          scale: newScale,
          width: item.width * effectiveFactor,
          depth: item.depth * effectiveFactor
        });
      },

      clearAllFurniture: () => set({ furnitureItems: {}, selectedFurnitureId: null, isDraggingFurniture: false, dragStartPos: null })
    }),
    {
      name: 'floorcraft-furniture-storage',
      partialize: (state) => ({ furnitureItems: state.furnitureItems }),
    }
  )
);

export default useFurnitureStore;